import { Link } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { COUNTRIES, DEFAULT_COUNTRY_CODE, isValidCountryCode } from '@/lib/auth/countries';
import { PASSWORD_REQUIREMENTS, assessPassword } from '@/lib/auth/passwords';
import { cn } from '@/lib/utils';

import { GoogleSignUpButton } from './GoogleSignUpButton';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

const SignUpSchema = z.object({
  email: z.string().min(1, 'Email is required').pipe(z.email('Enter a valid email')),
  name: z.string().min(1, 'Full name is required').max(120, 'Name is too long'),
  password: z
    .string()
    .min(
      PASSWORD_REQUIREMENTS.minLength,
      `At least ${String(PASSWORD_REQUIREMENTS.minLength)} characters required`,
    )
    .refine((value) => assessPassword(value).meetsRequirements, {
      message: 'Password must contain upper, lower, number, and symbol.',
    }),
  country: z
    .string()
    .min(1, 'Pick a country')
    .refine(isValidCountryCode, 'Pick a supported country'),
  marketingOptIn: z.boolean(),
});

export type SignUpFormValues = z.infer<typeof SignUpSchema>;

interface SignUpFormStepProps {
  /**
   * Called when the user submits a valid form. The wrapper handles the
   * actual Cognito call and the wizard transition; this step just hands
   * over the values.
   */
  readonly onSubmit: (values: SignUpFormValues) => Promise<void>;
  /** Top-of-form error message from the parent wizard (e.g. email-already-in-use). */
  readonly topError?: string | null;
}

export function SignUpFormStep({ onSubmit, topError }: SignUpFormStepProps) {
  const form = useForm<SignUpFormValues>({
    resolver: zodResolver(SignUpSchema),
    // onChange validation keeps `formState.isValid` accurate from the
    // first render onwards, so the Create-account button can disable
    // until every field passes. Trade-off: validation errors render
    // live as users type — matches modern signup UX (Stripe, Linear, …).
    mode: 'onChange',
    defaultValues: {
      email: '',
      name: '',
      password: '',
      country: DEFAULT_COUNTRY_CODE,
      marketingOptIn: false,
    },
  });

  const password = form.watch('password');
  const passwordAssessment = assessPassword(password);

  const submit = form.handleSubmit(async (values) => {
    await onSubmit(values);
  });

  // RHF tracks isSubmitting on `form.formState`; we just read from there.
  const isSubmitting = form.formState.isSubmitting;

  const emailError = form.formState.errors.email?.message;
  const nameError = form.formState.errors.name?.message;
  // No countryError surface: the Select only exposes codes from COUNTRIES,
  // and the Zod refinement `isValidCountryCode` is genuinely unreachable
  // through the UI. The refinement still runs and keeps the form's
  // `isValid` accurate as a defence-in-depth measure (a hacked DevTools
  // value would fail it), but there's no UI path that surfaces an error.
  // Password feedback comes entirely from the PasswordStrengthMeter — its
  // hint covers every failure mode (length + char-class variety) without
  // duplicating the form-level error message.

  return (
    <div className="space-y-4">
      {topError ? (
        <Alert variant="destructive">
          <AlertDescription>{topError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="signup-email">Email</Label>
        <Input
          id="signup-email"
          type="email"
          autoComplete="email"
          aria-invalid={Boolean(emailError)}
          aria-describedby={emailError ? 'signup-email-error' : undefined}
          {...form.register('email')}
        />
        {emailError ? (
          <p id="signup-email-error" className="text-destructive text-xs">
            {emailError}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-name">Full name</Label>
        <Input
          id="signup-name"
          type="text"
          autoComplete="name"
          aria-invalid={Boolean(nameError)}
          aria-describedby={nameError ? 'signup-name-error' : undefined}
          {...form.register('name')}
        />
        {nameError ? (
          <p id="signup-name-error" className="text-destructive text-xs">
            {nameError}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-password">Password</Label>
        <Input
          id="signup-password"
          type="password"
          autoComplete="new-password"
          aria-invalid={!passwordAssessment.meetsRequirements && password.length > 0}
          {...form.register('password')}
        />
        {/*
          Always-mounted wrapper that animates its own height between 0 and
          auto via the grid-template-rows trick. The dialog above us reflows
          smoothly because this height change is continuous rather than a
          step. `showMeter` flips at 2+ chars so the meter doesn't appear
          for a single stray keystroke.
        */}
        <div
          // ease-[cubic-bezier(0.22,1,0.36,1)] is "easeOutQuart" — much
          // more pronounced deceleration than Tailwind's default ease-out.
          // Snaps open initially, settles softly toward the end.
          className={cn(
            'grid transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
            password.length >= 2 ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
          )}
          aria-hidden={password.length < 2}
        >
          <div className="overflow-hidden">
            <PasswordStrengthMeter assessment={passwordAssessment} />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-country">Country</Label>
        <Controller
          control={form.control}
          name="country"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger id="signup-country" className="w-full">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    <span className="flex items-center gap-2.5">
                      <country.Flag
                        title={country.name}
                        // 24x16 is an exact 3:2 match for the SVG viewBox —
                        // any other ratio letterboxes the flag inside the
                        // SVG. Inline style instead of Tailwind w-6 h-4
                        // because shadcn's SelectTrigger has a
                        // `[&_svg:not([class*='size-'])]:size-4` selector
                        // that wins on specificity and forces 16x16 square
                        // when the selected flag renders inside the trigger
                        // via SelectValue.
                        style={{ width: 21, height: 14 }}
                        className="rounded-xs"
                      />
                      <span>{country.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div className="flex items-start gap-2">
        <Controller
          control={form.control}
          name="marketingOptIn"
          render={({ field }) => (
            <Checkbox
              id="signup-marketing"
              checked={field.value}
              onCheckedChange={(checked) => {
                field.onChange(checked === true);
              }}
              className="mt-0.5"
            />
          )}
        />
        <Label
          htmlFor="signup-marketing"
          className="text-muted-foreground text-xs font-normal leading-snug"
        >
          Send me product updates and shipping tips. Unsubscribe any time.
        </Label>
      </div>

      <Button
        type="button"
        className="w-full"
        disabled={!form.formState.isValid || isSubmitting}
        onClick={() => void submit()}
      >
        {isSubmitting ? 'Creating account…' : 'Create account'}
      </Button>

      <div className="relative">
        <div className="border-border absolute inset-0 flex items-center">
          <span className="border-border w-full border-t" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-card text-muted-foreground px-2 text-xs uppercase tracking-wider">
            or
          </span>
        </div>
      </div>

      <GoogleSignUpButton />

      <p className="text-muted-foreground text-center text-xs">
        Already have an account?{' '}
        <Link to="/signin" className="text-primary hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
